import React from 'react';
import { Alert, Button, Col, Form, Modal, Row } from 'react-bootstrap';
import { useDispatch } from 'react-redux';
import { fetchApi, makeResetCache, notifyError, notifyResponse } from 'gossamer-front-end';
import { convertDateTimeFromUTCToLocal } from 'gossamer-unified';
import { AdminRescheduleEventRequest, Event, LivestreamSession } from 'cb-model';

interface EventRescheduleModalProps {
  eventId: number;
  livestreamSessionId: string;
  firstStartAt: string;
  firstEndAt: string;
  show: boolean;
  setShow: (val: boolean) => void;
}

export const EventRescheduleModal = (props: EventRescheduleModalProps) => {
  const startDateLocal = convertDateTimeFromUTCToLocal(props.firstStartAt);
  const endDateLocal = convertDateTimeFromUTCToLocal(props.firstEndAt);
  const [date, setDate] = React.useState(startDateLocal.substring(0, 10));
  const [start, setStart] = React.useState(startDateLocal.substring(11, 19));
  const [end, setEnd] = React.useState(endDateLocal.substring(11, 19));
  const [error, setError] = React.useState<string>(undefined);
  const [processing, setProcessing] = React.useState(false);
  const minDate = convertDateTimeFromUTCToLocal(new Date().toISOString()).substring(0, 10);
  const dispatch = useDispatch();
  const resetCache = makeResetCache(dispatch);

  const validate = () => {
    setError(undefined);
    if (!date || !start || !end) {
      setError('all fields must be supplied');
      return;
    }
    if (end < start) {
      setError('end time must be after start time');
      return;
    }
  };

  React.useEffect(() => {
    validate();
  }, [date, start, end]);

  React.useEffect(() => {
    setDate(startDateLocal.substring(0, 10));
    setStart(startDateLocal.substring(11, 19));
    setEnd(endDateLocal.substring(11, 19));
  }, [props.show]);

  const handleSave = async () => {
    if (error) {
      return;
    }
    setProcessing(true);
    try {
      const response = await fetchApi('POST', `/main/event-reschedule`, {
        eventId: props.eventId,
        startDate: new Date([date, start].join('T')).toISOString(),
        endDate: new Date([date, end].join('T')).toISOString(),
      } as AdminRescheduleEventRequest);
      resetCache(Event.id, String(props.eventId));
      resetCache(LivestreamSession.id, String(props.livestreamSessionId));
      notifyResponse(response);
      props.setShow(false);
    } catch (e) {
      notifyError(e, 'An error occurred while rescheduling the event');
    }
    setProcessing(false);
  };
  return (
    <Modal
      show={props.show}
      onHide={() => props.setShow(false)}
      aria-labelledby="event-reschedule-modal-title"
      size="sm"
      fullscreen="md-down"
    >
      <Modal.Header closeButton>
        <Modal.Title id="event-reschedule-modal-title">Reschedule</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form>
          <Row>
            <Col>
              <Form.Group className="mb-3" controlId="date">
                <Form.Label>Date</Form.Label>
                <Form.Control type="date" value={date} onChange={(e) => setDate(e.currentTarget.value)} min={minDate} />
              </Form.Group>
            </Col>
            <Col>
              <Form.Group className="mb-3" controlId="start-time">
                <Form.Label>Start Time</Form.Label>
                <Form.Control type="time" value={start} onChange={(e) => setStart(e.currentTarget.value)} />
              </Form.Group>
            </Col>
            <Col>
              <Form.Group className="mb-3" controlId="end-time">
                <Form.Label>End Time</Form.Label>
                <Form.Control type="time" value={end} onChange={(e) => setEnd(e.currentTarget.value)} />
              </Form.Group>
            </Col>
          </Row>
          <Row>
            <Col>
              <Button variant="primary" onClick={handleSave} disabled={processing}>
                Save
              </Button>
            </Col>
          </Row>
          {!!error && <Alert variant="danger">{error}</Alert>}
        </Form>
      </Modal.Body>
    </Modal>
  );
};
